import { useCallback, useMemo, useState } from 'react';
import {
  contactsSchema, makeItemsSchema, makePaymentSchema, makeRouteSchema, validateForm,
} from '../../lib/validation';
import { totalDue, totalPaid, totalPrice, totalVolumeCbm, totalWeight } from './pricing';

export const STEP_IDS = ['route', 'contacts', 'items', 'payment'];

export const MAX_ITEMS = 50;

// Chaque colis porte son contenu ET son règlement : deux colis d'un même envoi
// peuvent être payés différemment.
export const createEmptyItem = () => ({
  id: `item-${Math.random().toString(36).slice(2, 10)}`,
  description: '',
  weight: '',
  length_cm: '',
  width_cm: '',
  height_cm: '',
  price: '',
  payment_status: '',
  advance_amount: '',
  payment_method: '',
  photos: [],
});

export function createEmptyForm({ transportMode = 'aerien', originCountry = '' } = {}) {
  return {
    transport_mode: transportMode,
    origin_country: originCountry,
    destination_country: '',
    destination_city: '',
    sender: { name: '', phone: '', email: '' },
    recipient: { name: '', phone: '', email: '' },
    merchandise_category_id: '',
    items: [createEmptyItem()],
    charter: { inspectionConsent: false, termsAccepted: false, noProhibitedItems: false },
  };
}

const str = (v) => (v === null || v === undefined ? '' : String(v));

/** Pré-remplissage depuis un colis existant (édition ou brouillon QR). */
export function packageToForm(pkg, { transportMode = 'aerien', originCountry = '' } = {}) {
  return {
    ...createEmptyForm({ transportMode, originCountry }),
    transport_mode: pkg.transport_mode || transportMode,
    origin_country: pkg.origin_country || originCountry,
    destination_country: pkg.destination_country || '',
    destination_city: pkg.destination_city || '',
    sender: {
      name: pkg.sender_name || '', phone: pkg.sender_phone || '', email: pkg.sender_email || '',
    },
    recipient: {
      name: pkg.recipient_name || '', phone: pkg.recipient_phone || '', email: pkg.recipient_email || '',
    },
    merchandise_category_id: pkg.merchandise_category_id || '',
    items: [packageToItem(pkg)],
  };
}

/** Un colis existant devient une ligne du formulaire, règlement compris. */
export function packageToItem(pkg) {
  return {
    ...createEmptyItem(),
    id: pkg.id || createEmptyItem().id,
    package_id: pkg.id,
    description: pkg.description || '',
    weight: str(pkg.weight),
    length_cm: str(pkg.length_cm),
    width_cm: str(pkg.width_cm),
    height_cm: str(pkg.height_cm),
    price: str(pkg.price),
    payment_status: pkg.payment_status || 'unpaid',
    advance_amount: str(pkg.advance_amount),
    payment_method: pkg.payment_method || '',
  };
}

/** Plusieurs colis d'une même déclaration : un seul formulaire, N lignes. */
export function packagesToForm(packages, options = {}) {
  const [first] = packages;
  const base = packageToForm(first || {}, options);
  return { ...base, items: packages.map(packageToItem) };
}

const setPath = (form, path, value) => {
  const [head, tail] = path.split('.');
  if (!tail) return { ...form, [head]: value };
  return { ...form, [head]: { ...form[head], [tail]: value } };
};

export function usePackageForm({ variant = 'partner', initialForm } = {}) {
  const [form, setForm] = useState(initialForm || createEmptyForm());
  const [errors, setErrors] = useState({});

  const setField = useCallback((path, value) => {
    setForm((prev) => setPath(prev, path, value));
  }, []);

  const setItem = useCallback((index, key, value) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((it, i) => (i === index ? { ...it, [key]: value } : it)),
    }));
  }, []);

  const addItem = useCallback(() => {
    setForm((prev) => (prev.items.length >= MAX_ITEMS
      ? prev
      : { ...prev, items: [...prev.items, createEmptyItem()] }));
  }, []);

  const removeItem = useCallback((index) => {
    setForm((prev) => (prev.items.length <= 1
      ? prev
      : { ...prev, items: prev.items.filter((_, i) => i !== index) }));
  }, []);

  // Compteur de colis : ajuste la liste sans perdre les colis déjà décrits.
  const setItemCount = useCallback((raw) => {
    let count = parseInt(raw, 10);
    if (Number.isNaN(count) || count < 1) count = 1;
    if (count > MAX_ITEMS) count = MAX_ITEMS;
    setForm((prev) => {
      if (count === prev.items.length) return prev;
      if (count < prev.items.length) return { ...prev, items: prev.items.slice(0, count) };
      const extra = Array.from({ length: count - prev.items.length }, createEmptyItem);
      return { ...prev, items: [...prev.items, ...extra] };
    });
  }, []);

  const validateStep = useCallback((stepId) => {
    const byStep = {
      route: () => validateForm(makeRouteSchema(variant), {
        transport_mode: form.transport_mode,
        origin_country: form.origin_country,
        destination_country: form.destination_country,
        destination_city: form.destination_city,
      }),
      contacts: () => validateForm(contactsSchema, { sender: form.sender, recipient: form.recipient }),
      items: () => validateForm(makeItemsSchema(form.transport_mode), { items: form.items }),
      payment: () => validateForm(makePaymentSchema(variant), { items: form.items }),
    };
    const result = byStep[stepId]();
    setErrors(result.errors || {});
    return result.success;
  }, [form, variant]);

  const totals = useMemo(() => ({
    weight: totalWeight(form.items),
    volumeCbm: totalVolumeCbm(form.items),
    count: form.items.length,
    price: totalPrice(form.items),
    paid: totalPaid(form.items),
    due: totalDue(form.items),
  }), [form.items]);

  const reset = useCallback((next) => {
    setForm(next || createEmptyForm({
      transportMode: form.transport_mode,
      originCountry: form.origin_country,
    }));
    setErrors({});
  }, [form.transport_mode, form.origin_country]);

  return {
    form, setForm, setField, setItem, setItemCount, addItem, removeItem,
    errors, setErrors, validateStep, totals, reset,
  };
}
